import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ADMIN } from '@/lib/adminApi';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Search, Phone, Mail, Eye } from 'lucide-react';

const STATUSES = ['new', 'contacted', 'quoted', 'won', 'lost'];
const STATUS_COLORS = {
  new: 'bg-blue-50 text-blue-700 border-blue-200',
  contacted: 'bg-amber-50 text-amber-700 border-amber-200',
  quoted: 'bg-violet-50 text-violet-700 border-violet-200',
  won: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  lost: 'bg-slate-100 text-slate-600 border-slate-200',
};

const IS_DEV = process.env.NODE_ENV === 'development';

export default function AdminLeads() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [noteText, setNoteText] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ADMIN.leads({ status: statusFilter || undefined, q: q || undefined });
      setRows(r.leads || []);
    } catch (err) {
      toast.error('Failed to load leads');
      if (IS_DEV) {
        // eslint-disable-next-line no-console
        console.error('AdminLeads reload failed', err);
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, q]);

  useEffect(() => { reload(); }, [reload]);

  const openLead = useCallback(async (r) => {
    // Open immediately with what we have, then upgrade to full record (photos, etc.)
    setSelected(r);
    if (!r._id || !r.source) return;
    try {
      const full = await ADMIN.fullLead(r.source, r._id);
      setSelected((prev) => (prev && prev._id === r._id ? full : prev));
    } catch (err) {
      // Non-fatal: dialog still shows the summary fields fetched in the list.
      if (IS_DEV) {
        // eslint-disable-next-line no-console
        console.error('Failed to load full lead detail', err);
      }
    }
  }, []);

  const onStatus = useCallback(async (lead, status) => {
    try {
      await ADMIN.updateLeadStatus(lead._id, lead.source, status);
      toast.success('Status updated');
      reload();
    } catch (err) {
      toast.error('Update failed');
      if (IS_DEV) {
        // eslint-disable-next-line no-console
        console.error('AdminLeads status update failed', err);
      }
    }
  }, [reload]);

  const addNote = useCallback(async () => {
    if (!noteText.trim() || !selected) return;
    try {
      await ADMIN.addLeadNote(selected._id, selected.source, noteText);
      toast.success('Note added');
      setNoteText('');
      const r = await ADMIN.leads({ status: statusFilter || undefined });
      const found = (r.leads || []).find((x) => x._id === selected._id);
      if (found) setSelected(found);
      setRows(r.leads || []);
    } catch (err) {
      toast.error('Failed');
      if (IS_DEV) {
        // eslint-disable-next-line no-console
        console.error('AdminLeads add note failed', err);
      }
    }
  }, [noteText, selected, statusFilter]);

  return (
    <div className="space-y-5" data-testid="admin-leads-view">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Leads</h1>
          <p className="text-sm text-slate-500 mt-1">Wizard, residential, commercial, insurance.</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="leads-search" placeholder="Search name/email/phone" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && reload()} className="pl-9 w-full sm:w-72" />
          </div>
          <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-44" data-testid="leads-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </header>

      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
        {loading ? <Skeleton className="h-64" /> : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="leads-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Contact</th>
                  <th className="px-4 py-3 font-semibold">Service</th>
                  <th className="px-4 py-3 font-semibold">Source</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500">No leads yet.</td></tr>}
                {rows.map((r) => (
                  <tr key={`${r.source}-${r._id}`} className="hover:bg-slate-50" data-testid={`lead-row-${r._id}`}>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{r.name || '—'}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="flex flex-col gap-0.5">
                        {r.phone && <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 hover:text-[#0A2540]"><Phone className="h-3 w-3" />{r.phone}</a>}
                        {r.email && <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-[#0A2540] truncate max-w-[200px]"><Mail className="h-3 w-3" />{r.email}</a>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{r.service || '—'}</td>
                    <td className="px-4 py-3 capitalize text-slate-600 text-xs">{r.source}</td>
                    <td className="px-4 py-3">
                      <Select value={r.status || 'new'} onValueChange={(v) => onStatus(r, v)}>
                        <SelectTrigger className={`h-8 w-32 capitalize text-xs font-semibold border ${STATUS_COLORS[r.status] || ''}`} data-testid={`lead-status-${r._id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>))}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="ghost" onClick={() => openLead(r)} data-testid={`lead-view-${r._id}`}><Eye className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">{selected?.name || 'Lead'}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <Info label="Source" value={selected.source} />
                <Info label="Service" value={selected.service} />
                <Info label="Phone" value={selected.phone} link={`tel:${selected.phone}`} />
                <Info label="Email" value={selected.email} link={`mailto:${selected.email}`} />
                <Info label="Address" value={(() => {
                  const w = selected.wizard_answers || {};
                  const street = w.address || w.property_address || w.full_address;
                  const cityStateZip = [w.city, w.state, selected.zip_code || w.zip_code || w.zip].filter(Boolean).join(', ');
                  return [street, cityStateZip].filter(Boolean).join(' — ') || selected.zip_code || '—';
                })()} />
                <Info label="Status" value={selected.status} />
              </div>
              {(selected.wizard_answers?.photos || []).length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-slate-900 mb-2">Attached Photos ({selected.wizard_answers.photos.length})</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {selected.wizard_answers.photos.map((p, i) => (
                      <a
                        key={`${p.filename || 'photo'}-${i}`}
                        href={`data:${p.content_type || 'image/jpeg'};base64,${p.base64}`}
                        download={p.filename || `photo-${i + 1}.jpg`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative block rounded-lg overflow-hidden bg-slate-100 aspect-square border border-slate-200 hover:ring-2 hover:ring-[#0A2540] transition-shadow"
                        data-testid={`lead-photo-${i}`}
                        title={p.filename}
                      >
                        <img src={`data:${p.content_type || 'image/jpeg'};base64,${p.base64}`} alt={p.filename || `photo-${i + 1}`} loading="lazy" className="h-full w-full object-cover" />
                        <div className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[10px] px-1.5 py-0.5 truncate">{p.filename || `photo-${i + 1}`}</div>
                      </a>
                    ))}
                  </div>
                  {selected.has_photos && !selected.wizard_answers.photos.some((p) => p.base64) && (
                    <p className="mt-2 text-xs text-slate-500">Loading photos…</p>
                  )}
                </div>
              )}
              {selected.has_photos && !selected.wizard_answers?.photos?.length && (
                <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2">
                  {selected.photo_count || 0} photo(s) attached — loading…
                </div>
              )}
              {selected.wizard_answers && Object.keys(selected.wizard_answers).filter((k) => k !== 'photos').length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-slate-900 mb-2">Submission details</h3>
                  <div className="rounded-lg bg-slate-50 p-3 text-sm space-y-1">
                    {Object.entries(selected.wizard_answers).filter(([k]) => k !== 'photos').map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-3 border-b border-slate-200 pb-1 last:border-0">
                        <span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}</span>
                        <span className="text-slate-800 font-medium text-right break-words max-w-[60%]">{Array.isArray(v) ? v.join(', ') : String(v ?? '—')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <h3 className="text-sm font-bold text-slate-900 mb-2">Notes ({selected.notes?.length || 0})</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {(selected.notes || []).map((n, i) => (
                    <div key={`${n.timestamp || ''}-${n.author || ''}-${i}`} className="rounded-lg bg-slate-50 p-3 text-sm">
                      <div className="text-xs text-slate-500">{n.author} • {n.timestamp ? new Date(n.timestamp).toLocaleString() : ''}</div>
                      <div className="text-slate-700 mt-1">{n.text}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <Textarea rows={2} placeholder="Add a note (e.g., Called 4/12, no answer)" value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="lead-note-input" />
                  <Button onClick={addNote} className="bg-[#0A2540] hover:bg-[#13294a]" data-testid="lead-note-add">Add</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value, link }) {
  return (
    <div className="rounded-lg bg-slate-50 p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5 break-words">
        {link ? <a href={link} className="text-[#0A2540] hover:underline">{value || '—'}</a> : (value || '—')}
      </div>
    </div>
  );
}
